import escape from 'lodash/escape'
import type { QuoteDocument, OptionsDocument } from 'paintscout'
import { combineContexts, createQuoteContext, createUserContext, type Context } from '@paintscout/util/templater'

// Fields whose value is intentionally pre-composed HTML (e.g. joined with `<br/>`), not plain
// text — these are excluded from escaping below.
const RAW_HTML_KEYS = new Set(['estimatorInfo'])

/**
 * Builds the real-value template context used to resolve `{{key}}` tags in presentation
 * text. Presentations are viewed publicly (customer-view), so the "estimator" fields come
 * from the quote's owner rather than a logged-in user.
 *
 * Context values (contact name, address, company fields, etc.) are user-editable and end up
 * substituted directly into HTML that's rendered via `dangerouslySetInnerHTML`. `template()`
 * does raw string substitution with no escaping, and `HtmlContent`'s sanitizer only filters
 * tag names — it doesn't strip attributes off allowed tags. So values must be HTML-escaped
 * here, before they ever reach `template()`, or a field like a contact's name could carry a
 * stored XSS payload (e.g. `<img src=x onerror=...>`) into the public presentation.
 */
export function getPresentationTemplateContext(args: { quote?: QuoteDocument; options?: OptionsDocument }): Context {
  const { quote, options } = args
  const context = combineContexts(createQuoteContext({ quote, options }), createUserContext({ user: quote?.owner }))

  return Object.fromEntries(
    Object.entries(context).map(([key, value]) => [
      key,
      typeof value === 'string' && !RAW_HTML_KEYS.has(key) ? escape(value) : value
    ])
  )
}
